import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import "./alert-styles.css";

const TokenReceipt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenData, setTokenData] = useState(null);
  const [alertShown, setAlertShown] = useState(false);
  const receiptRef = useRef(null);

  const showSuccessAlert = () => {
    const alertDiv = document.createElement('div');
    alertDiv.className = 'alert-override success';
    alertDiv.innerHTML = `
      <strong>Token Booked Successfully!</strong>
      Your token has been generated. Please show this receipt at the clinic.
    `;

    document.body.appendChild(alertDiv);

    setTimeout(() => {
      alertDiv.classList.add('fade-out');
      setTimeout(() => {
        if (alertDiv.parentNode) {
          alertDiv.remove();
        }
      }, 300);
    }, 6000);

    alertDiv.addEventListener('click', () => {
      alertDiv.classList.add('fade-out');
      setTimeout(() => {
        if (alertDiv.parentNode) {
          alertDiv.remove();
        }
      }, 300);
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // Get token data from navigation state
    if (location.state?.tokenData) {
      setTokenData(location.state.tokenData);
      // Show success alert when token data is loaded (only once)
      if (!alertShown) {
        showSuccessAlert();
        setAlertShown(true);
      }
    } else {
      // Fallback to localStorage if no navigation state
      const stored = localStorage.getItem("tokenly_last_token");
      if (stored) {
        try {
          const data = JSON.parse(stored);
          setTokenData(data);
          // Show success alert when token data is loaded (only once)
          if (!alertShown) {
            showSuccessAlert();
            setAlertShown(true);
          }
        } catch (e) {
          console.error("Invalid token data:", e);
        }
      }
    }
  }, [location.state, alertShown]);

  if (!tokenData) {
    return (
      <div className="tokenbook-page container py-5 text-center">
        <div className="alert alert-warning">
          No token data found. Please book a token first.
        </div>
        <a href="/alldoctors" className="btn btn-primary mt-3">Book Token</a>
      </div>
    );
  }

  const now = new Date();
  const formattedDate = now.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const bookedAt = now.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const tokenLabel = tokenData.token_no_prefix
    ? `${tokenData.token_no_prefix}-${tokenData.token_no}`
    : tokenData.token_no;

  // Unified capture function for both download and share
  const captureReceipt = async (purpose = 'download') => {
    if (!receiptRef.current) return;

    try {
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: window.devicePixelRatio || 2,
        logging: false,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: false,
        imageTimeout: 15000,
        width: receiptRef.current.offsetWidth,
        height: receiptRef.current.offsetHeight
      });

      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Blob is null - canvas tainted');
          if (purpose === 'share') fallbackTextShare();
          return;
        }

        const fileName = `tokenly-receipt-${tokenLabel}.png`;
        const file = new File([blob], fileName, { type: 'image/png' });

        // Try Web Share API for mobile (shows WhatsApp in share sheet)
        if (purpose === 'share' && navigator.share && navigator.canShare?.({ files: [file] })) {
          navigator.share({
            title: 'Tokenly - Token Receipt',
            text: `🏥 Token: ${tokenLabel}\n👨‍⚕️ Doctor: ${tokenData.doctorName || "Doctor"}\n⏰ ${tokenData.shift_time}`,
            files: [file]
          }).catch((err) => {
            console.log('Share cancelled or failed:', err);
            downloadBlob(blob, fileName);
            if (purpose === 'share') fallbackWhatsAppText();
          });
        } else {
          // Desktop fallback: Download image + open WhatsApp text
          downloadBlob(blob, fileName);
          if (purpose === 'share') fallbackWhatsAppText();
        }
      }, 'image/png');
    } catch (error) {
      console.error('html2canvas failed:', error);
      if (purpose === 'share') fallbackTextShare();
    }
  };

  // Download blob as file
  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // WhatsApp text share (always works)
  const fallbackWhatsAppText = () => {
    const shareText = `🏥 Tokenly - Token Receipt\n\n📋 Token Number: ${tokenLabel}\n👨‍⚕️ Doctor: ${tokenData.doctorName || "Doctor"}\n⏰ Time: ${tokenData.shift_time}\n📅 Date: ${formattedDate}\n📱 Mobile: ${tokenData.mobile}\n\nBook your tokens easily with Tokenly! 🎉`;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  const fallbackTextShare = fallbackWhatsAppText;

  return (
    <div className="token-receipt-page">
      <div className="token-receipt-card" ref={receiptRef}>
        <div className="token-receipt-header">
          <h3>Tokenly</h3>
          <p className="token-receipt-subtitle">Online Token System</p>
        </div>

        <div className="token-receipt-meta">
          <div>{formattedDate}</div>
          <div className="token-receipt-booked">
            ● Booked at: <span>{bookedAt}</span>
          </div>
        </div>

        <div className="token-receipt-number">{tokenLabel}</div>

        <hr className="token-receipt-divider" />

        <div className="token-receipt-row">
          <div>
            <div className="token-receipt-label">Doctor</div>
            <div className="token-receipt-value">
              {tokenData.doctorName || "Doctor"}
            </div>
          </div>
          <div className="token-receipt-right">
            <div className="token-receipt-label">Specialty</div>
            <div className="token-receipt-value">
              {tokenData.speciality || "Dermatologist"}
            </div>
          </div>
        </div>

        <div className="token-receipt-row">
          <div>
            <div className="token-receipt-label">Time Slot</div>
            <div className="token-receipt-strong">
              {tokenData.shift_time}
            </div>
          </div>
          <div className="token-receipt-right">
            <div className="token-receipt-label">Patients</div>
            <div className="token-receipt-value">
              {tokenData.patients_count || 1}
            </div>
          </div>
        </div>

        <div className="token-receipt-mobile">
          <div className="token-receipt-label">Registered Mobile</div>
          <div className="token-receipt-mobile-value">
            {tokenData.mobile}
          </div>
        </div>

        <div className="token-receipt-note">
          Please show this token at reception.
        </div>

        <div className="d-flex gap-2 justify-content-center">
          <button
            className="token-receipt-print-btn"
            onClick={() => captureReceipt('download')}
            style={{ background: '#059669', border: 'none', borderRadius: '999px', padding: '12px 16px' }}
            title="Download And Share"
          >
            <i className="fa-solid fa-share-nodes"></i>
          </button>

          <button
            className="token-receipt-print-btn"
            onClick={() => navigate('/trackii')}
            style={{ background: '#f59e0b', border: 'none', borderRadius: '999px', padding: '12px 16px' }}
            title="Track Your Token"
          >
            <i className="fa-solid fa-location-dot"></i>
          </button>

          {/* <button
            className="token-receipt-print-btn"
            onClick={() => captureReceipt('share')}
            style={{ background: '#25D366', border: 'none', borderRadius: '999px', padding: '12px 16px' }}
            title="Share on WhatsApp"
          >
            <i className="fa-brands fa-whatsapp"></i>
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default TokenReceipt;
